import { ColorType, ThemeType } from "../types";
import Traditional, { TraditionalColors } from "./Traditional";

declare type BlueColorType = ColorType & {
  lightblue: string;
  greyblue: string;
  cerulean: string;
  navy: string;
  th: string;
  "slate-blue": string;
  "ice-blue": string;
  "ocean-blue": string;
  "light-blue-grey": string;
  "core-icon-active": string;
  "core-icon-dark": string;
  "primary-button-border": string;
  "primary-button-background": string;
};

export const BlueColors: BlueColorType = {
  ...TraditionalColors,
  lightblue: "#71c4fa",
  "ocean-blue": "#0363a2",
  "light-blue-grey": "#b8cde6",
  greyblue: "#8593a3",
  cerulean: "#0281d4",
  "ice-blue": "#ebf3f9",
  slate: "#455e6c",
  th: "#ccd9e7",
  navy: "#031e42",
  "mlb-navy": "#041e42",
  "mlb-red": "#bf0d3e",
  green: "#00a651",
  "core-icon-active": "#4c8cee",
  "core-icon-dark": "#002e6d",
  "slate-blue": "#819bba",
  "primary-button-background": "#01132c",
  "primary-button-border": "#354d6e",
};

const FontWeights = {
  light: "100",
  normal: "400",
  bold: "500",
};

declare type BlueThemeType = ThemeType & {
  colors: BlueColorType;
};

const Blue: BlueThemeType = {
  ...Traditional,
  name: "Blue",
  fontFamily: '"Roboto", sans-serif',
  colors: BlueColors,
  label: {
    ...Traditional.label,
    color: BlueColors["slate-blue"],
    "asterisk-color": BlueColors["mlb-red"],
    "font-weight": "normal",
    size: "0.75rem",
    display: "flex",
    "asterisk-margin": "0rem 0rem 0rem 0.5625rem",
  },
  input: {
    color: BlueColors.navy,
    size: "0.875rem",
    height: "auto",
    "border-color": BlueColors["light-blue-grey"],
    background: BlueColors.white,
    required: {
      color: BlueColors.navy,
      background: BlueColors.white,
      "border-color": BlueColors["light-blue-grey"],
    },
    missed: {
      color: BlueColors["mlb-red"],
      background: BlueColors.white,
      "border-color": BlueColors["mlb-red"],
    },
    disabled: {
      color: BlueColors.black,
      background: BlueColors.snow,
      "border-color": BlueColors.fog,
    },
  },
  select: {
    ...Traditional.select,
    actionColor: BlueColors["navy"],
  },
  button: {
    "border-radius": "0.375rem",
    size: "0.875rem",
    padding: "0.3125rem .75rem",
    "line-height": "1.5",
    "font-weight": FontWeights.bold,
    primary: {
      "border-color": BlueColors["primary-button-border"],
      "background-color": BlueColors["primary-button-background"],
      color: BlueColors["light-blue-grey"],
    },
    disabled: {
      "border-color": BlueColors["light-blue-grey"],
      "background-color": BlueColors["ice-blue"],
      color: BlueColors["light-blue-grey"],
    },
    //TODO: Set proper colors for other button types as needed
    secondary: {
      "border-color": BlueColors["primary-button-border"],
      "background-color": BlueColors["primary-button-background"],
      color: BlueColors["light-blue-grey"],
    },
    danger: {
      "border-color": BlueColors["primary-button-border"],
      "background-color": BlueColors["primary-button-background"],
      color: BlueColors["light-blue-grey"],
    },
    loading: {
      "border-color": BlueColors["primary-button-border"],
      "background-color": BlueColors["primary-button-background"],
      color: BlueColors["light-blue-grey"],
    },
    default: {
      "border-color": BlueColors["primary-button-border"],
      "background-color": BlueColors["primary-button-background"],
      color: BlueColors["light-blue-grey"],
    },
    success: {
      "border-color": BlueColors["primary-button-border"],
      "background-color": BlueColors["primary-button-background"],
      color: BlueColors["light-blue-grey"],
    },
    small: {
      size: "0.6875rem",
      padding: "0.25rem .75rem",
    },
    large: {
      size: "1.125rem",
      padding: "0.25rem 1rem",
    },
    spinner: {
      size: 15,
    },
  },
  modal: {
    ...Traditional.modal,
    borderColor: BlueColors.navy,
    backdrop: BlueColors.navy,
    backdropOpacity: 0,
    header: {
      ...Traditional.modal.header,
      background: BlueColors.th,
      bottomBorderColor: BlueColors.navy,
      fontColor: BlueColors["mlb-navy"],
      fontSize: "24px",
      fontWeight: FontWeights.normal,
    },
    body: { ...Traditional.modal.body, background: BlueColors["ice-blue"], fontColor: BlueColors["mlb-navy"] },
    footer: { ...Traditional.modal.footer, background: BlueColors.th, borderTopColor: BlueColors.navy },
  },
  navigation: {
    vertical: {
      ...Traditional.navigation.vertical,
      size: "0.875rem",
      fontSize: "0.875rem",
      background: BlueColors.navy,
      borderColor: BlueColors.black,
      color: BlueColors.white,
      active: {
        color: BlueColors.navy,
        background: BlueColors.lightblue,
      },
      hover: {
        color: BlueColors.navy,
        background: BlueColors.lightblue,
      },
      signout: {
        color: BlueColors.navy,
        background: BlueColors.lightblue,
        borderBottomColor: BlueColors.white,
      },
      usernameDropdown: {
        color: BlueColors.navy,
        background: BlueColors.white,
        hover: {
          color: BlueColors.navy,
          background: BlueColors.white,
        },
      },
      topBar: {
        color: BlueColors.white,
        background: BlueColors.navy,
      },
      sublinks: {
        color: BlueColors.lightblue,
        background: BlueColors.black,
        hover: {
          color: BlueColors.navy,
          background: BlueColors.lightblue,
        },
      },
      sublinksMenu: {
        padding: "0",
      },
    },
    horizontal: {
      ...Traditional.navigation.horizontal,
      active: {
        color: BlueColors.navy,
        background: BlueColors.lightblue,
      },
      mobile: {
        ...Traditional.navigation.horizontal.mobile,
        dropdownColor: BlueColors.white,
        dropdownBackground: BlueColors["mlb-navy"],
        sublinkColor: BlueColors.lightblue,
        sublinkBackground: BlueColors.black,
        permissionsBackground: BlueColors.lightblue,
        permissionsColor: BlueColors.navy,
      },
      sublinks: {
        color: BlueColors.lightblue,
        background: BlueColors.black,
        hover: {
          color: BlueColors.lightblue,
          background: BlueColors.black,
        },
      },
    },
  },
  calendar: {
    header: {
      color: BlueColors.navy,
      background: BlueColors.th,
      fontFamily: "Roboto",
      fontWeight: "normal",
      size: "0.6875rem",
    },
    body: {
      color: BlueColors.black,
      background: BlueColors.white,
      borderColor: BlueColors.greyblue,
      fontFamily: "RobotoSlab",
      size: "1.125rem",
    },
    actions: {
      background: BlueColors.white,
      color: BlueColors.navy,
      activeColor: BlueColors.lightblue,
    },
  },
};

export default Blue;
